import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { OrgMember, Organization, OutstandingOrgInvite, OutstandingOrgEmailInvite } from '@heybesafe/shared';
import { encryptFamilyHandoff, entitlementErrorOr } from '@heybesafe/shared';
import { useSafe } from '../../context/SafeContext';
import { useFamilyKeys } from '../../context/FamilyKeysContext';
import { deleteOrg, finalizeEmailInvite, getInviteeKey, getOrg, inviteMember, listOrgInvites, promoteMember, removeMember, revokeOrgInvite } from '../../api/client';
import { base64UrlToBytes } from '../../crypto/base64url';
import { accountKeyAuthentic } from '../../pkastore/accountKeyAuth';
import { getPinnedIdentity, pinIdentity } from '../../pkastore/pinnedIdentities';
import type { InviteeKeyResponse } from '@heybesafe/shared';
import Button from '../../components/Button';
import { useEntitlement } from '../../hooks/useEntitlement';
import { ProBadge } from '../../components/ProBadge';
import { ProModal } from '../../components/ProModal';
import { EmailNotVerifiedModal } from '../../components/EmailNotVerifiedModal';
import { AddSeatsCTA } from '../../components/AddSeatsCTA';
import { MyFamilyNameCard } from './MyFamilyNameCard';
import { useMemberNames, memberLabel } from '../../hooks/useMemberNames';

interface OrgDetail {
  org: Organization;
  members: OrgMember[];
}

// Stable empty-array reference for when `detail` hasn't loaded yet — see
// useMemberNames' JSDoc: an inline `[]` fallback would be a fresh reference
// on every render and re-trigger the hook's effect for no reason.
const EMPTY_MEMBERS: OrgMember[] = [];

/**
 * Security-review finding C1 (kill-switch for the measure-then-enforce rollout).
 *
 * ENFORCING (2026-10-09): inviting a member who presents no PKA identity is a
 * hard block, so a server that strips the PKA fields can no longer silently
 * downgrade the family-key handoff to a key it controls. Flipped to `true`
 * after the Phase-3 scan (`server/scripts/count-pre-pka-accounts.ts`) confirmed
 * the pre-PKA population was an acceptable, drainable set. Flip back to `false`
 * to revert to the dark/grandfather behavior (grandfather + a greppable
 * `InviteWouldBlockNoPka` warning) if a regression surfaces — reversible with
 * no crypto redeploy. See
 * `docs/superpowers/plans/2026-10-09-pka-fail-open-hardening-plan.md` Phase 3.
 */
export const PKA_INVITE_ENFORCE = true;

/**
 * PKA site A1 — authenticate an invitee's account key before wrapping the family
 * private key to it. Returns an error string to BLOCK the handoff, or null to
 * proceed.
 *
 * Family sharing uses TOFU (pin on first use, block on change) — we do not
 * require an out-of-band safety-number check here (that's the recovery flow).
 * A member who has published a PKA identity must present a valid account-key
 * signature by that identity.
 *
 * Finding C1: a member with NO published identity used to be grandfathered
 * (unless already pinned), which let a malicious server strip the PKA fields
 * from the lookup and force the handoff through to a server-controlled key. Once
 * `enforce` is on, that path is BLOCKED instead — matching `verifyFamilyKeySignature`'s
 * no-grandfather rule. Until then it warns (`InviteWouldBlockNoPka`) and proceeds,
 * so the rollout can be measured first.
 */
export function authenticateInvitee(
  lookup: InviteeKeyResponse,
  enforce: boolean = PKA_INVITE_ENFORCE,
): string | null {
  const hasPka = !!lookup.identityPublicKey && !!lookup.accountPublicKeySig;
  const pinned = getPinnedIdentity(lookup.userId);
  if (!hasPka) {
    if (pinned) {
      // A now-unsigned key for a member we've pinned is a downgrade — always block.
      return "This member's security key can no longer be verified — do not send the family key. Ask them to sign in to the latest version.";
    }
    if (enforce) {
      return "This member hasn't set up their security key yet. Ask them to sign in to the latest version of Hey Be Safe, then try the invite again.";
    }
    // Dark period: measure how often enforcement WOULD block before turning it on.
    console.warn('[pka] InviteWouldBlockNoPka: invitee has no published security key (grandfathered while PKA_INVITE_ENFORCE is off)');
    return null;
  }
  if (!accountKeyAuthentic(lookup)) {
    return "Could not verify this member's security key — do not send the family key. Ask them to sign in to the latest version.";
  }
  if (pinIdentity(lookup.userId, lookup.identityPublicKey!).changed) {
    return "This member's security key changed since you last saw it — re-verify with them before sending the family key.";
  }
  return null;
}

export function ManageTab() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { orgs, refreshOrgs, refreshPendingFinalizations, loginEmail } = useSafe();
  const familyKeys = useFamilyKeys();
  const { canInviteMember, state: entitlementState, snapshot } = useEntitlement();
  // canInviteMember is false both for non-subscribers AND for subscribers whose
  // seat pool (shared across every family they own) is full. Only the former
  // needs the PRO badge / subscribe modal; the latter needs Add seats — the
  // same split the server makes with NO_ENTITLEMENT_INVITE_MEMBER vs POOL_FULL.
  const needsSubscription = entitlementState !== 'trial' && entitlementState !== 'active-subscriber';
  const seatPoolFull = !needsSubscription && !canInviteMember
    && !!snapshot?.seatPool && snapshot.seatPool.used >= snapshot.seatPool.capacity;

  const myOrg = orgs.find((o) => o.id === orgId);
  const isAdmin = myOrg?.role === 'admin';

  // Route guard: non-admin → Overview. Must target /overview explicitly: the
  // family index now redirects to Manage, so bouncing to it would loop.
  useEffect(() => {
    if (myOrg && !isAdmin && orgId) {
      navigate(`/orgs/${orgId}/overview`, { replace: true });
    }
  }, [myOrg, isAdmin, orgId, navigate]);

  const [detail, setDetail]   = useState<OrgDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  // Invite
  const [inviteEmail, setInviteEmail]               = useState('');
  const [inviteMessage, setInviteMessage]           = useState('');
  const [inviteError, setInviteError]               = useState('');
  const [inviteAtCap, setInviteAtCap]               = useState(false);
  const [inviting, setInviting]                     = useState(false);
  const [proModalOpen, setProModalOpen]             = useState(false);
  const [emailNotVerifiedOpen, setEmailNotVerifiedOpen] = useState(false);

  // Remove member
  const [removingId, setRemovingId]           = useState<string | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  // Promote member
  const [promotingId, setPromotingId] = useState<string | null>(null);

  // Delete org
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting]           = useState(false);

  // Pending invitations
  const [pendingInvitations, setPendingInvitations] = useState<{
    regular: OutstandingOrgInvite[];
    email: OutstandingOrgEmailInvite[];
  }>({ regular: [], email: [] });
  const [pendingInvitationsLoading, setPendingInvitationsLoading] = useState(false);

  // Finalize email-invite
  const [finalizingToken, setFinalizingToken] = useState<string | null>(null);
  const [finalizeError, setFinalizeError] = useState<string>('');

  const refetchPendingInvitations = useCallback(async () => {
    if (!orgId || !isAdmin) return;
    setPendingInvitationsLoading(true);
    try {
      const result = await listOrgInvites(orgId);
      if (result.ok) {
        setPendingInvitations({
          regular: result.data.invites,
          email: result.data.emailInvites,
        });
      }
    } finally {
      setPendingInvitationsLoading(false);
    }
  }, [orgId, isAdmin]);

  useEffect(() => {
    void refetchPendingInvitations();
  }, [refetchPendingInvitations]);

  // Reused by the mount effect below AND by MyFamilyNameCard's onSaved, so
  // saving/clearing your own name updates the "Family Members" roster right
  // below it without the expensive refreshOrgs() (listOrgs + a getOrg per
  // org) and without a full page reload.
  const refetchDetail = useCallback(async () => {
    if (!orgId || !isAdmin) return;
    const result = await getOrg(orgId);
    if (result.ok) {
      setDetail(result.data as OrgDetail);
    } else {
      setError(result.error);
    }
  }, [orgId, isAdmin]);

  useEffect(() => {
    setLoading(true);
    void refetchDetail().finally(() => setLoading(false));
  }, [refetchDetail]);

  const names = useMemberNames(orgId, detail?.members ?? EMPTY_MEMBERS, detail?.org, familyKeys);

  // Render nothing while we're redirecting non-admins.
  if (!myOrg || !isAdmin) return null;

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!orgId || !inviteEmail.trim()) return;
    setInviteError('');
    setInviteMessage('');
    setInviting(true);
    try {
      const email = inviteEmail.trim();

      const lookup = await getInviteeKey(orgId, email);

      // CASE A: existing HBS user (today's flow).
      if (lookup.ok) {
        const familyPriv = familyKeys.getKey(orgId);
        if (!familyPriv) {
          setInviteError('Sign in again to invite.');
          return;
        }
        // PKA (A1): authenticate the invitee's account key before wrapping the
        // family private key to it, so a malicious server can't substitute a key
        // it controls and read the family's shared items.
        const authError = authenticateInvitee(lookup.data);
        if (authError) { setInviteError(authError); return; }
        const familyKeyWrap = await encryptFamilyHandoff(familyPriv, base64UrlToBytes(lookup.data.accountPublicKey));
        const result = await inviteMember(orgId, { email, familyKeyWrap });
        if (!result.ok) {
          if (result.code === 'EMAIL_NOT_VERIFIED') { setEmailNotVerifiedOpen(true); return; }
          if (result.code === 'POOL_FULL') { setInviteAtCap(true); setInviteError(''); return; }
          if (result.code === 'ALREADY_MEMBER') { setInviteError('Already a member of this family.'); return; }
          setInviteAtCap(false);
          setInviteError(entitlementErrorOr(result.code, result.error));
          return;
        }
        // The server still returns the token in result.data.invite.token; we
        // intentionally ignore it — the invitee consumes it via the pending-
        // invites UI on their next sign-in.
        setInviteAtCap(false);
        setInviteMessage(`Invitation sent to ${email}. They'll see it next time they sign in.`);
        setInviteEmail('');
        await refetchPendingInvitations();
        return;
      }

      // CASE B: non-user → email-invite branch.
      if (lookup.code === 'NOT_A_USER') {
        const result = await inviteMember(orgId, { email });   // NO familyKeyWrap
        if (!result.ok) {
          if (result.code === 'EMAIL_NOT_VERIFIED') { setEmailNotVerifiedOpen(true); return; }
          if (result.code === 'POOL_FULL') { setInviteAtCap(true); setInviteError(''); return; }
          setInviteAtCap(false);
          setInviteError(entitlementErrorOr(result.code, result.error));
          return;
        }
        setInviteAtCap(false);
        setInviteMessage(`Email invitation sent to ${email}. They'll need to sign up at heybesafe.com — you'll see them under "Awaiting your confirmation" once they do.`);
        setInviteEmail('');
        await refetchPendingInvitations();
        return;
      }

      // CASE C: other lookup errors.
      setInviteError(
        lookup.code === 'ALREADY_MEMBER' ? 'Already a member of this family.' : lookup.error,
      );
    } finally {
      setInviting(false);
    }
  }

  async function handleRevokeInvite(token: string) {
    if (!orgId) return;
    const result = await revokeOrgInvite(orgId, token);
    if (result.ok) {
      await refetchPendingInvitations();
    }
  }

  async function handleFinalize(emailInvite: OutstandingOrgEmailInvite) {
    if (!orgId) return;
    setFinalizeError('');
    setFinalizingToken(emailInvite.token);
    try {
      const familyPriv = familyKeys.getKey(orgId);
      if (!familyPriv) {
        setFinalizeError('Sign in again to finalize.');
        return;
      }
      const lookup = await getInviteeKey(orgId, emailInvite.invitedEmail);
      if (!lookup.ok) {
        setFinalizeError(`Couldn't fetch ${emailInvite.invitedEmail}'s account key: ${lookup.error}`);
        return;
      }
      // PKA (A1): authenticate the invitee's account key before wrapping the
      // family private key to it (same guarantee as the direct-invite path).
      const authError = authenticateInvitee(lookup.data);
      if (authError) { setFinalizeError(authError); return; }
      const familyKeyWrap = await encryptFamilyHandoff(
        familyPriv,
        base64UrlToBytes(lookup.data.accountPublicKey),
      );
      const result = await finalizeEmailInvite(orgId, emailInvite.token, {
        familyKeyWrap,
        inviteeUserId: lookup.data.userId,
      });
      if (!result.ok) {
        setFinalizeError(result.error);
        return;
      }
      await refetchPendingInvitations();
      // Clear the Families nav attention highlight without waiting for the
      // next refreshOrgs / reload — this admin just acted on the signal.
      void refreshPendingFinalizations();
    } finally {
      setFinalizingToken(null);
    }
  }

  async function handleRemoveMember(userId: string) {
    if (!orgId) return;
    setRemovingId(userId);
    try {
      const result = await removeMember(orgId, userId);
      if (result.ok) {
        setDetail((prev) => prev ? { ...prev, members: prev.members.filter((m) => m.userId !== userId) } : prev);
        setConfirmRemoveId(null);
      }
    } finally {
      setRemovingId(null);
    }
  }

  async function handlePromote(targetUserId: string) {
    if (!orgId) return;
    setPromotingId(targetUserId);
    try {
      const result = await promoteMember(orgId, targetUserId);
      if (!result.ok) {
        setError(result.error);
        return;
      }
      // Optimistically reflect the role change locally, plus refresh the
      // global orgs list (mirrors the delete-org flow).
      setDetail((prev) => prev ? {
        ...prev,
        members: prev.members.map((m) => m.userId === targetUserId ? { ...m, role: 'admin' as const } : m),
      } : prev);
      await refreshOrgs();
    } catch (e) {
      console.error('Promote failed', e);
      setError('Failed to promote — please try again.');
    } finally {
      setPromotingId(null);
    }
  }

  async function handleDeleteOrg() {
    if (!orgId) return;
    setDeleting(true);
    try {
      const result = await deleteOrg(orgId);
      if (result.ok) {
        await refreshOrgs();
        navigate('/orgs');
      }
    } finally {
      setDeleting(false);
    }
  }

  if (loading) {
    return <div className="text-hbs-text-mute text-sm">Loading…</div>;
  }

  if (error || !detail) {
    return (
      <div className="text-hbs-text-mute text-sm">
        {error || 'Family not found.'}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Mounted here too, not just OverviewTab: OrgCreatePage lands the
          founder on this tab (see mount note in OverviewTab.tsx), and this
          admin needs to be able to set their own name just like any member. */}
      <MyFamilyNameCard orgId={orgId!} onSaved={() => void refetchDetail()} />
      {/* Members */}
      <section>
        <h2 className="text-sm font-semibold text-hbs-text-mute uppercase tracking-wide mb-3">Family Members</h2>
        <div className="bg-hbs-surface rounded-hbs-xl border border-hbs-border overflow-hidden">
          {detail.members.map((member, idx) => (
            <div key={member.userId} className={`flex items-center gap-3 px-4 py-3 ${idx < detail.members.length - 1 ? 'border-b border-hbs-border' : ''}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-hbs-text truncate">{memberLabel(member, names)}</p>
                {names.get(member.userId)?.trim() && (
                  <p className="text-xs text-hbs-text-mute truncate">{member.email}</p>
                )}
                <p className="text-xs text-hbs-text-mute">
                  {member.role === 'admin' ? 'Admin' : 'Member'} · Joined {new Date(member.joinedAt).toLocaleDateString()}
                </p>
              </div>
              {member.role === 'member' && (
                <button
                  id={`member-promote-${member.userId}`}
                          onClick={() => handlePromote(member.userId)}
                  disabled={promotingId === member.userId}
                  className="flex-shrink-0 px-2.5 py-1.5 text-xs text-hbs-text-mute hover:text-hbs-primary hover:bg-hbs-primary/10 rounded-hbs-md transition-colors disabled:opacity-50"
                >
                  {promotingId === member.userId ? '…' : 'Promote to admin'}
                </button>
              )}
              {member.role !== 'admin' && (
                confirmRemoveId === member.userId ? (
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      id={`member-remove-confirm-${member.userId}`}
                            onClick={() => handleRemoveMember(member.userId)}
                      disabled={removingId === member.userId}
                      aria-label="Confirm remove"
                      className="px-2.5 py-1.5 text-xs bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-hbs-md transition-colors disabled:opacity-50"
                    >
                      {removingId === member.userId ? '…' : 'Confirm remove'}
                    </button>
                    <button
                      id={`member-remove-cancel-${member.userId}`}
                            onClick={() => setConfirmRemoveId(null)}
                      className="px-2.5 py-1.5 text-xs bg-hbs-surface-alt hover:bg-hbs-surface-alt text-hbs-text rounded-hbs-md transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    id={`member-remove-${member.userId}`}
                          onClick={() => setConfirmRemoveId(member.userId)}
                    aria-label="Remove"
                    className="flex-shrink-0 px-2.5 py-1.5 text-xs text-hbs-text-mute hover:text-red-400 hover:bg-red-400/10 rounded-hbs-md transition-colors"
                  >
                    Remove
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Invite */}
      <section>
        <h2 className="text-sm font-semibold text-hbs-text-mute uppercase tracking-wide mb-3">Invite to Family</h2>
        <div className="bg-hbs-surface rounded-hbs-xl border border-hbs-border p-4">
          <form onSubmit={handleInvite} className="flex flex-col gap-3">
            <input
              type="email"
              aria-label="Email to invite"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="Email to invite"
              required
              className="w-full bg-hbs-bg border border-hbs-border rounded-hbs-md px-3 py-2.5 text-sm text-hbs-text placeholder-hbs-text-mute focus:outline-none focus:border-hbs-primary focus:ring-1 focus:ring-hbs-primary transition-colors"
            />
            {inviteAtCap || seatPoolFull ? (
              <div className="mt-2 p-3 bg-hbs-surface rounded-hbs-md border border-hbs-border text-sm">
                <p className="text-hbs-text mb-2">
                  You've used all the seats in your subscription. Add a 5-seat pack to invite more members.
                </p>
                <AddSeatsCTA label="Add seats" variant="inline" />
              </div>
            ) : inviteError ? (
              <p className="text-red-400 text-xs">{inviteError}</p>
            ) : null}
            <Button
              type={canInviteMember ? 'submit' : 'button'}
              onClick={needsSubscription ? () => setProModalOpen(true) : undefined}
              disabled={seatPoolFull}
              loading={inviting}
              aria-label="Invite"
            >
              Invite
              {needsSubscription && <ProBadge />}
            </Button>
          </form>
          {inviteMessage && (
            <div className="mt-3 p-3 bg-hbs-primary/10 border border-hbs-primary/20 rounded-hbs-md text-sm text-hbs-text">
              {inviteMessage}
            </div>
          )}
        </div>
      </section>

      {/* Pending invitations */}
      <section className="mt-6">
        <h2 className="text-sm font-semibold text-hbs-text-mute uppercase tracking-wide mb-3">Pending Invitations</h2>

        {pendingInvitationsLoading ? (
          <p className="text-xs text-hbs-text-mute">Loading…</p>
        ) : (
          <>
            {/* Sent: regular invites + awaiting-registration email-invites */}
            {(() => {
              const sentEmails = pendingInvitations.email.filter((e) => e.state === 'awaiting-registration');
              const allSent: Array<{ token: string; invitedEmail: string; createdAt: string; expiresAt: string; _kind: 'regular' | 'email' }> = [
                ...pendingInvitations.regular.map((r) => ({ ...r, _kind: 'regular' as const })),
                ...sentEmails.map((e) => ({ ...e, _kind: 'email' as const })),
              ];
              if (allSent.length === 0) return null;
              return (
                <>
                  <h3 className="text-xs font-medium text-hbs-text-mute mb-2">Sent</h3>
                  <ul className="space-y-2 mb-4">
                    {allSent.map((inv) => (
                      <li key={`${inv._kind}-${inv.token}`} className="flex items-center justify-between p-3 bg-hbs-surface rounded-hbs-md border border-hbs-border">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-hbs-text truncate">{inv.invitedEmail}</div>
                          <div className="text-xs text-hbs-text-mute mt-0.5">
                            Invited {new Date(inv.createdAt).toLocaleDateString()} · Expires {new Date(inv.expiresAt).toLocaleDateString()}
                          </div>
                        </div>
                        <button
                          type="button"
                          id={`invite-revoke-${inv.token}`}
                          onClick={() => void handleRevokeInvite(inv.token)}
                          className="ml-3 text-xs text-hbs-text-mute hover:text-hbs-text underline"
                        >
                          Revoke
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              );
            })()}

            {/* Awaiting your confirmation: awaiting-finalization email-invites */}
            {(() => {
              const awaiting = pendingInvitations.email.filter((e) => e.state === 'awaiting-finalization');
              if (awaiting.length === 0) return null;
              return (
                <>
                  <h3 className="text-xs font-medium text-hbs-text-mute mb-2 flex items-center gap-1.5">
                    <span
                      aria-label="This section needs your attention"
                      className="inline-block w-2 h-2 rounded-full bg-hbs-danger"
                    />
                    Awaiting your confirmation
                  </h3>
                  <ul className="space-y-2 mb-2">
                    {awaiting.map((inv) => (
                      <li key={`await-${inv.token}`} className="flex items-center justify-between p-3 bg-hbs-surface rounded-hbs-md border border-hbs-border">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-hbs-text truncate">{inv.invitedEmail}</div>
                          <div className="text-xs text-hbs-text-mute mt-0.5">
                            They've registered — finalize to send their family key.
                          </div>
                        </div>
                        <div className="flex gap-2 ml-3">
                          <button
                            type="button"
                            id={`invite-finalize-${inv.token}`}
                        onClick={() => void handleFinalize(inv)}
                            disabled={finalizingToken === inv.token}
                            className="px-3 py-1.5 text-xs bg-hbs-primary hover:bg-hbs-primary-hover text-white rounded-hbs-md font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Finalize
                          </button>
                          <button
                            type="button"
                            id={`invite-revoke-${inv.token}`}
                            onClick={() => void handleRevokeInvite(inv.token)}
                            className="text-xs text-hbs-text-mute hover:text-hbs-text underline"
                          >
                            Revoke
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {finalizeError && (
                    <p role="alert" className="text-xs text-red-600 mt-2">{finalizeError}</p>
                  )}
                </>
              );
            })()}

            {pendingInvitations.regular.length === 0 && pendingInvitations.email.length === 0 && (
              <p className="text-xs text-hbs-text-mute">No pending invitations.</p>
            )}
          </>
        )}
      </section>

      {/* Danger zone */}
      <section>
        <h2 className="text-sm font-semibold text-red-400 uppercase tracking-wide mb-3">Danger Zone</h2>
        <div className="bg-hbs-surface rounded-hbs-xl border border-red-500/20 p-4">
          <p className="text-sm text-hbs-text-mute mb-3">
            Permanently delete this family. This cannot be undone.
          </p>
          {!confirmDelete ? (
            <Button variant="danger" onClick={() => setConfirmDelete(true)}>Delete Family</Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="danger" loading={deleting} onClick={handleDeleteOrg}>Confirm Delete</Button>
              <Button variant="secondary" onClick={() => setConfirmDelete(false)}>Cancel</Button>
            </div>
          )}
        </div>
      </section>

      {proModalOpen && <ProModal feature="invite" onClose={() => setProModalOpen(false)} />}
      {emailNotVerifiedOpen && loginEmail && (
        <EmailNotVerifiedModal
          email={loginEmail}
          onClose={() => setEmailNotVerifiedOpen(false)}
        />
      )}
    </div>
  );
}
